import { gql } from '@provision/graphql-client';

// this is a custom mutation that returns a JSON object with the conversation context
// it does not join these fields as relations, it is simple select from object thing
const CONVERSATION_CONTEXT_FIELDS_FRAGMENT = gql`
  fragment ConversationContextFields on GetConversationContextResponse {
    subject
    status
    type
    urgency
    labels
    requester
    partner {
      businessName
      timeZone
    }
    participants {
      agents
      clients {
        name
        primaryPhoneNumber
        primaryEmail
        internalNotes
      }
    }
    messages {
      author
      role
      createdAt
      text
      rawBody
      isInternal
      attachments {
        type
        title
        content
      }
    }
    associatedSites {
      address
      serviceLevel
      cppExpDate
      internalSiteNotes
      internalMembershipNotes
      activationFlowUrl
      associatedConversations {
        friendlyId
        subject
        status
      }
    }
  }
`;

export const GET_CONVERSATION_CONTEXT_FOR_MARKDOWN_EXPORT_QUERY = gql`
  query GetConversationContextForMarkdownExport($input: GetConversationContextInput!) {
    getConversationContext(input: $input) {
      ...ConversationContextFields
    }
  }
  ${CONVERSATION_CONTEXT_FIELDS_FRAGMENT}
`;

export const GET_BULK_CONVERSATION_CONTEXT_FOR_MARKDOWN_EXPORT_QUERY = gql`
  query GetBulkConversationContextForMarkdownExport($input: GetBulkConversationContextInput!) {
    getBulkConversationContext(input: $input) {
      results {
        conversationId
        error
        context {
          ...ConversationContextFields
        }
      }
    }
  }
  ${CONVERSATION_CONTEXT_FIELDS_FRAGMENT}
`;

export interface ConversationContextAttachment {
  type?: string | null;
  title?: string | null;
  content?: string | null;
}

export interface ConversationContextMessage {
  author?: string | null;
  role?: string | null;
  createdAt?: string | null;
  text?: string | null;
  isInternal?: boolean | null;
  attachments?: ConversationContextAttachment[] | null;
  rawBody?: string | null;
}

export interface ConversationContextClientParticipant {
  name?: string | null;
  primaryPhoneNumber?: string | null;
  primaryEmail?: string | null;
  internalNotes?: string | null;
}

export interface ConversationContextParticipants {
  agents?: (string | null)[] | null;
  clients?: ConversationContextClientParticipant[] | null;
}

export interface ConversationContextPartner {
  businessName?: string | null;
  timeZone?: string | null;
}

export interface ConversationContextAssociatedConversation {
  friendlyId?: string | null;
  subject?: string | null;
  status?: string | null;
}

export interface ConversationContextSite {
  address?: string | null;
  serviceLevel?: string | null;
  cppExpDate?: string | null;
  internalSiteNotes?: string | null;
  internalMembershipNotes?: string | null;
  activationFlowUrl?: string | null;
  associatedConversations?: ConversationContextAssociatedConversation[] | null;
}

export interface ConversationContextResponse {
  subject?: string | null;
  status?: string | null;
  type?: string | null;
  urgency?: string | null;
  labels?: (string | null)[] | null;
  requester?: string | null;
  partner?: ConversationContextPartner | null;
  participants?: ConversationContextParticipants | null;
  messages?: ConversationContextMessage[] | null;
  associatedSites?: ConversationContextSite[] | null;
}

export interface GetConversationContextForMarkdownExportQuery {
  getConversationContext?: ConversationContextResponse | null;
}

export interface GetConversationContextForMarkdownExportVariables {
  input: {
    conversationId: string;
    partnerId?: number;
  };
}

export interface BulkConversationContextResponseItem {
  conversationId: string;
  error?: string | null;
  context?: ConversationContextResponse | null;
}

export interface GetBulkConversationContextForMarkdownExportQuery {
  getBulkConversationContext?: {
    results: BulkConversationContextResponseItem[];
  } | null;
}

export interface GetBulkConversationContextForMarkdownExportVariables {
  input: {
    conversationIds: string[];
    partnerId?: number;
  };
}
